import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiMail } from "react-icons/fi";
import { useAuth } from "./authContext";
import {
  AuthField,
  AuthFooterLinks,
  AuthSectionTitle,
  AuthStatusPanel,
  NEW_PASSWORD_PATTERN,
  PasswordChecklist,
  PasswordInput,
  authButtonClass,
  authInputClass,
  authPageLinkClass,
} from "./authPageShell";
import { ResendVerificationButton } from "./authVerifyEmail";
import CoolDownButton from "./coolDownButton";
import { t, useI18n } from "./utils/i18n";
import { getLastModulePath } from "./utils/lastModule";

// Kept as an inline style: `font: inherit` lets the switch link adapt to each
// surrounding context's font-size (footer 0.92rem vs hint 0.8rem).
const switchButtonStyle = {
  border: "none",
  background: "transparent",
  color: "#38bdf8",
  cursor: "pointer",
  font: "inherit",
  fontWeight: 800,
  padding: 0,
};

// Shared Tailwind class for the footer "switch mode" / secondary-link blocks.
const authFooterBlockClass =
  "mt-4 border-t border-[rgba(148,163,184,0.18)] pt-4 text-[0.92rem] leading-[1.7] text-slate-400";

function AuthFormMessage({ message, onDismiss }) {
  if (!message) return null;

  return (
    <div
      role="alert"
      className="mb-4 flex justify-between gap-3 rounded-control border border-[rgba(248,113,113,0.24)] bg-[rgba(127,29,29,0.32)] px-[0.95rem] py-[0.85rem] text-[0.9rem] leading-[1.5] text-[#fecaca]"
    >
      <span>{message}</span>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 cursor-pointer self-start whitespace-nowrap border-none bg-transparent font-extrabold text-[#fecaca]"
        >
          {t("auth.common.close")}
        </button>
      ) : null}
    </div>
  );
}

function useFormMessage() {
  const [message, setMessage] = useState("");
  return {
    message,
    showError: setMessage,
    clearMessage: () => setMessage(""),
  };
}

function GoToSignInButton({ onSwitchMode, secondary = false }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => (onSwitchMode ? onSwitchMode("login") : navigate("/"))}
      className={authButtonClass(false, secondary)}
    >
      {t("auth.common.go_to_sign_in")}
    </button>
  );
}

const getResponseMessage = (json, fallbackKey) =>
  json?.messageKey ? t(json.messageKey) : json?.message || t(fallbackKey);

function ModeLink({ to, mode, onSwitchMode, children }) {
  if (onSwitchMode) {
    return (
      <button type="button" onClick={() => onSwitchMode(mode)} style={switchButtonStyle}>
        {children}
      </button>
    );
  }

  return (
    <Link to={to} className={authPageLinkClass}>
      {children}
    </Link>
  );
}

export function LoginForm({ onSwitchMode, onSuccess, showMeta = false }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loginMessage, setLoginMessage] = useState("");
  // Set when sign-in is refused for an unverified email, to offer a fresh link.
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const nameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const { isAuthenticated, refreshAuth, authConnectionError } = useAuth();
  const { language: currentLanguage } = useI18n();
  const translate = (key) => t(key);
  const appVersion = process.env.REACT_APP_VERSION;
  const supportEmail = process.env.REACT_APP_EMAIL;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(getLastModulePath());
    } else if (localStorage.getItem("showLoggedOutAlert")) {
      setLoginMessage(translate("auth.login.session_expired"));
      localStorage.removeItem("showLoggedOutAlert");
    }
  }, [isAuthenticated, navigate, currentLanguage]);

  useEffect(() => {
    const shouldShowLogoutAlert = localStorage.getItem("showLoggedOutAlert");
    if (shouldShowLogoutAlert) {
      setLoginMessage(translate("auth.login.logged_out"));
      localStorage.removeItem("showLoggedOutAlert");
    }
  }, [navigate, currentLanguage]);

  useEffect(() => {
    if (!authConnectionError) return;
    setLoginMessage(authConnectionError);
  }, [authConnectionError]);

  useEffect(() => {
    if (!isCooldown || cooldownRemaining <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          setIsCooldown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [cooldownRemaining, isCooldown]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || isCooldown) return;
    setUnverifiedEmail("");
    if (!name || !password) {
      setLoginMessage(
        !name ? translate("auth.login.missing_username") : translate("auth.login.missing_password")
      );
      !name ? nameInputRef.current.focus() : passwordInputRef.current.focus();
      return;
    }
    setIsLoading(true);
    setIsCooldown(true);
    setCooldownRemaining(5);
    handleLogin();
  };

  const handleLogin = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/login", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: name, password }),
        credentials: "include",
      });
      if (!response.ok) {
        const json = await response.json();
        if (response.status === 400 || response.status === 403) {
          setPassword("");
          if (json.messageKey === "auth.login.email_not_verified" && name.includes("@")) {
            setUnverifiedEmail(name.trim());
          }
          return setLoginMessage(json.messageKey ? translate(json.messageKey) : json.message);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      if (json.success) {
        await refreshAuth({ silent: true });
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(getLastModulePath());
        }
      }
    } catch (err) {
      setLoginMessage(
        err instanceof TypeError
          ? translate("auth.connection.local_https_error")
          : translate("auth.login.server_error")
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AuthFormMessage message={loginMessage} onDismiss={() => setLoginMessage("")} />
      {unverifiedEmail ? (
        <div className="mb-4">
          <ResendVerificationButton email={unverifiedEmail} />
        </div>
      ) : null}
      <AuthSectionTitle
        title={translate("auth.login.section_title")}
        description={translate("auth.login.section_description")}
      />
      <form onSubmit={handleFormSubmit} noValidate>
        <AuthField label={translate("auth.field.username")}>
          <input
            ref={nameInputRef}
            disabled={isLoading}
            type="text"
            autoComplete="username"
            onChange={(e) => setName(e.target.value)}
            value={name}
            className={authInputClass}
          />
        </AuthField>
        <AuthField
          label={translate("auth.field.password")}
          hint={
            <ModeLink to="/" mode="reset" onSwitchMode={onSwitchMode}>
              {translate("auth.login.forgot_password")}
            </ModeLink>
          }
        >
          <PasswordInput
            ref={passwordInputRef}
            disabled={isLoading}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
          />
        </AuthField>
        <div className="mt-5">
          <CoolDownButton
            type="submit"
            text={translate("auth.login.submit")}
            loadingText={translate("loading")}
            waitText={translate("auth.login.cooldown")}
            isLoading={isLoading}
            disabled={isLoading || isCooldown}
            remainingSeconds={cooldownRemaining}
          />
        </div>
        {onSwitchMode ? (
          <div className={authFooterBlockClass}>
            <div>
              {translate("auth.login.footer_prompt")}{" "}
              <ModeLink to="/" mode="register" onSwitchMode={onSwitchMode}>
                {translate("auth.register.section_title")}
              </ModeLink>
            </div>
            <div className="mt-[0.4rem]">
              {translate("auth.login.footer_secondary")}{" "}
              <ModeLink to="/" mode="reset" onSwitchMode={onSwitchMode}>
                {translate("auth.reset.section_title")}
              </ModeLink>
            </div>
          </div>
        ) : (
          <AuthFooterLinks
            prompt={translate("auth.login.footer_prompt")}
            linkTo="/"
            linkText={translate("auth.register.section_title")}
            secondaryLink={
              <>
                {translate("auth.login.footer_secondary")}{" "}
                <ModeLink to="/" mode="reset" onSwitchMode={onSwitchMode}>
                  {translate("auth.reset.section_title")}
                </ModeLink>
              </>
            }
          />
        )}
      </form>
      {showMeta && (appVersion || supportEmail) ? (
        <div className="mt-4 text-center text-[0.8rem] leading-[1.7] text-slate-400">
          {appVersion ? <span>{translate("auth.login.version_label")} {appVersion}</span> : null}
          {appVersion && supportEmail ? <span> · </span> : null}
          {supportEmail ? (
            <a href={`mailto:${supportEmail}`} className="font-semibold text-blue-300 no-underline">
              {supportEmail}
            </a>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export function RegisterForm({ onSwitchMode }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [alias, setAlias] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  // The address the verification email went to; once set, the form gives way to "check your inbox".
  const [createdEmail, setCreatedEmail] = useState("");
  const { message, showError, clearMessage } = useFormMessage();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  useI18n();
  const translate = (key) => t(key);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/grimoire");
    }
  }, [isAuthenticated, navigate]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      return showError(translate("auth.register.invalid_email"));
    }
    if (!NEW_PASSWORD_PATTERN.test(password)) {
      return showError(translate("auth.password_rule.invalid"));
    }
    if (!termsAccepted) {
      return showError(translate("auth.register.terms_required"));
    }
    handleRegister();
  };

  const handleRegister = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/signup", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        // An allowlisted email needs no code, so a blank one is left out.
        body: JSON.stringify({ email, alias, password, termsAccepted, inviteCode: inviteCode.trim() || undefined }),
        credentials: "include",
      });
      if (!response.ok) {
        const json = await response.json();
        if (response.status === 400 || response.status === 403) {
          return showError(getResponseMessage(json, "auth.login.server_error"));
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      if (json.success) {
        setPassword("");
        setInviteCode("");
        clearMessage();
        setCreatedEmail(email.trim());
        setIsAuthenticated(false);
      }
    } catch {
      showError(translate("auth.login.server_error"));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  if (createdEmail) {
    const [beforeEmail, afterEmail = ""] = translate("auth.register.check_inbox_description").split("{email}");
    return (
      <AuthStatusPanel
        icon={FiMail}
        title={translate("auth.register.check_inbox_title")}
        description={
          <>
            {beforeEmail}
            <strong className="break-all text-slate-50">{createdEmail}</strong>
            {afterEmail}
          </>
        }
      >
        <GoToSignInButton onSwitchMode={onSwitchMode} />
        {/* The signup just sent one, so the API would refuse another for a minute. */}
        <ResendVerificationButton email={createdEmail} initialCooldown={60} />
      </AuthStatusPanel>
    );
  }

  return (
    <>
      <AuthFormMessage message={message} onDismiss={clearMessage} />
      <AuthSectionTitle title={translate("auth.register.section_title")} />
      <form onSubmit={handleFormSubmit}>
        <AuthField label={translate("auth.field.email")}>
          <input
            type="email"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            className={authInputClass}
          />
        </AuthField>
        <AuthField label={translate("auth.field.alias")}>
          <input
            type="text"
            autoComplete="nickname"
            onChange={(e) => setAlias(e.target.value)}
            value={alias}
            className={authInputClass}
          />
        </AuthField>
        <AuthField label={translate("auth.field.password")}>
          <PasswordInput autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} value={password} />
          <PasswordChecklist password={password} />
        </AuthField>
        <AuthField label={translate("auth.field.invite_code")}>
          <input
            type="text"
            placeholder={translate("auth.register.invite_placeholder")}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            onChange={(e) => setInviteCode(e.target.value)}
            value={inviteCode}
            className={authInputClass}
          />
        </AuthField>
        <label className="mx-0 mt-1 mb-4 flex items-start gap-[0.65rem] text-[0.88rem] leading-[1.55] text-slate-300">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1"
          />
          <span>
            {translate("auth.register.terms_prefix")}{" "}
            <Link to="/terms" target="_blank" rel="noreferrer" className={authPageLinkClass}>
              {translate("legal.terms")}
            </Link>{" "}
            {translate("auth.register.terms_and")}{" "}
            <Link to="/privacy" target="_blank" rel="noreferrer" className={authPageLinkClass}>
              {translate("legal.privacy")}
            </Link>
          </span>
        </label>
        <button type="submit" disabled={isLoading} className={authButtonClass(isLoading)}>
          {isLoading ? translate("auth.register.submitting") : translate("auth.register.submit")}
        </button>
      </form>
      {onSwitchMode ? (
        <div className={authFooterBlockClass}>
          {translate("auth.register.footer_prompt")}{" "}
          <button type="button" onClick={() => onSwitchMode("login")} style={switchButtonStyle}>
            {translate("auth.login.section_title")}
          </button>
        </div>
      ) : (
        <AuthFooterLinks
          prompt={translate("auth.register.footer_prompt")}
          linkTo="/"
          linkText={translate("auth.login.section_title")}
        />
      )}
    </>
  );
}

// Asks for an emailed reset link. The API answers the same whether or not the
// address has an account, so success always reads "check your inbox".
export function PasswordResetForm({ onSwitchMode }) {
  const [email, setEmail] = useState("");
  const [isSent, setIsSent] = useState(false);
  const { message, showError, clearMessage } = useFormMessage();
  const [isLoading, setIsLoading] = useState(false);
  useI18n();
  const translate = (key) => t(key);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      return showError(translate("auth.register.invalid_email"));
    }
    setIsLoading(true);
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/reset/request", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
        credentials: "include",
      });
      if (!response.ok) {
        const json = await response.json();
        if (response.status === 400) {
          return showError(getResponseMessage(json, "auth.login.server_error"));
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      if (json.success) {
        clearMessage();
        setIsSent(true);
      }
    } catch {
      showError(translate("auth.login.server_error"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSent) {
    return (
      <AuthStatusPanel
        icon={FiMail}
        title={translate("auth.reset.request_sent_title")}
        description={translate("auth.reset.request_sent")}
      >
        <GoToSignInButton onSwitchMode={onSwitchMode} />
        <button type="button" onClick={() => setIsSent(false)} className={authButtonClass(false, true)}>
          {translate("auth.reset.use_another_email")}
        </button>
      </AuthStatusPanel>
    );
  }

  return (
    <>
      <AuthFormMessage message={message} onDismiss={clearMessage} />
      <AuthSectionTitle
        title={translate("auth.reset.section_title")}
        description={translate("auth.reset.section_description")}
      />
      <form onSubmit={handleFormSubmit}>
        <AuthField label={translate("auth.field.email")}>
          <input
            type="email"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            className={authInputClass}
          />
        </AuthField>
        <button type="submit" disabled={isLoading} className={authButtonClass(isLoading)}>
          {isLoading ? translate("auth.reset.submitting") : translate("auth.reset.submit")}
        </button>
      </form>
      {onSwitchMode ? (
        <div className={authFooterBlockClass}>
          {translate("auth.reset.footer_prompt")}{" "}
          <button type="button" onClick={() => onSwitchMode("login")} style={switchButtonStyle}>
            {translate("auth.login.section_title")}
          </button>
        </div>
      ) : (
        <AuthFooterLinks
          prompt={translate("auth.reset.footer_prompt")}
          linkTo="/"
          linkText={translate("auth.login.section_title")}
        />
      )}
    </>
  );
}

// Sets the new password from an emailed reset link (`/reset-password?token=…`).
// The token is single-use; an invalid or expired one offers a fresh request.
export function PasswordResetConfirmForm({ token, onSwitchMode }) {
  const [password, setPassword] = useState("");
  const [isDone, setIsDone] = useState(false);
  const [isLinkInvalid, setIsLinkInvalid] = useState(!token);
  const { message, showError, clearMessage } = useFormMessage();
  const [isLoading, setIsLoading] = useState(false);
  useI18n();
  const translate = (key) => t(key);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!NEW_PASSWORD_PATTERN.test(password)) {
      return showError(translate("auth.password_rule.invalid"));
    }
    setIsLoading(true);
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/reset/confirm", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password }),
        credentials: "include",
      });
      const json = await response.json();
      if (!response.ok) {
        if (response.status === 400) {
          if (json.messageKey === "auth.reset.link_invalid") {
            clearMessage();
            return setIsLinkInvalid(true);
          }
          return showError(getResponseMessage(json, "auth.login.server_error"));
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (json.success) {
        setPassword("");
        clearMessage();
        setIsDone(true);
      }
    } catch {
      showError(translate("auth.login.server_error"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isDone) {
    return (
      <AuthStatusPanel
        tone="success"
        icon={FiCheckCircle}
        title={translate("auth.reset.success_title")}
        description={translate("auth.reset.success_description")}
      >
        <GoToSignInButton onSwitchMode={onSwitchMode} />
      </AuthStatusPanel>
    );
  }

  if (isLinkInvalid) {
    return (
      <AuthStatusPanel
        tone="error"
        icon={FiAlertCircle}
        title={translate("auth.reset.link_invalid_title")}
        description={translate("auth.reset.link_invalid")}
      >
        <button type="button" onClick={() => onSwitchMode("reset")} className={authButtonClass(false)}>
          {translate("auth.reset.request_new_link")}
        </button>
        <GoToSignInButton onSwitchMode={onSwitchMode} secondary />
      </AuthStatusPanel>
    );
  }

  return (
    <>
      <AuthFormMessage message={message} onDismiss={clearMessage} />
      <AuthSectionTitle
        title={translate("auth.reset.confirm_title")}
        description={translate("auth.reset.confirm_description")}
      />
      <form onSubmit={handleFormSubmit}>
        <AuthField label={translate("auth.field.new_password")}>
          <PasswordInput autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} value={password} />
          <PasswordChecklist password={password} />
        </AuthField>
        <button type="submit" disabled={isLoading} className={authButtonClass(isLoading)}>
          {isLoading ? translate("auth.reset.confirm_submitting") : translate("auth.reset.confirm_submit")}
        </button>
      </form>
      <div className={authFooterBlockClass}>
        {translate("auth.reset.footer_prompt")}{" "}
        <button type="button" onClick={() => onSwitchMode("login")} style={switchButtonStyle}>
          {translate("auth.login.section_title")}
        </button>
      </div>
    </>
  );
}
